<template>
  <v-container fill-height fluid class="pa-0 my-container">
    <div class="frame-container" v-if="projectLoaded">
      <!-- Show dialogs when activated -->
      <AttachmentDialog :dialog="currentAttachment"/>
      <HotspotDialog v-if="currentHotspot" :dialog="currentHotspot"/>
      <!-- Show after equirect playback has started -->
      <VideoControls
        v-if="contentType === 'video' && (playbackStarted && layout !== '2D')"
        class='video-controls'
      />
      <!-- Show immediately -->
      <ImageControls
        v-if="contentType === 'image' && layout !== '2D'"
        class='image-controls'
      />
      <!--  Show cover image before playback starts -->
      <VideoCover v-if="showVideoCover" />

      <template v-if="project">
        <!-- show immediately when project is loaded -->
        <Image2D
          v-if="layout === '2D' && contentType === 'image'"
          class="regular-projection"
          :key="id"
        />
        <!-- Show after video url has been set-->
        <Video2D
          v-if="regularVideoUrl"
          class="regular-projection"
          :key="id"
        />
        <!-- Show equirect immediately after project is loaded -->
        <iframe
          v-if="layout !== '2D'"
          class="player"
          :key="id"
          @load="onLoadIframe"
          :src="playerUrl"
          name="OrionPlayer"
          frameborder="0"
          allowfullscreen
          allow='autoplay'
          playsinline
          webkit-playsinline
        ></iframe>
      </template>
    </div>
  </v-container>
</template>

<script>
import { Logger } from 'aws-amplify';
import { mapState } from 'vuex';
import EventBus from '@/EventBus';
import isNumeric from '@/utils/helper';
import VideoControls from '@/components/controls/VideoControls.vue';
import ImageControls from '@/components/controls/ImageControls.vue';
import HotspotDialog from '@/components/dialogs/Hotspot.vue';
import AttachmentDialog from '@/components/dialogs/HotspotAttachment.vue';
import Image2D from '@/components/image2d/Content.vue';
import Video2D from '@/components/video2d/Content.vue';
import VideoCover from '@/components/overlays/VideoCover.vue';

const logger = new Logger('PlayerVue');

export default {
  name: 'player',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {};
  },
  components: {
    HotspotDialog,
    VideoControls,
    ImageControls,
    AttachmentDialog,
    VideoCover,
    Image2D,
    Video2D,
  },
  watch: {
    // call again the method if the route changes
    // eslint-disable-next-line
    '$route': 'resetProject',
  },
  created() {
    this.$eventHub.$on('reader-error', this.readerError);
  },
  beforeDestroy() {
    this.$eventHub.$off('reader-error');
  },
  async mounted() {
    if (!this.project) {
      try {
        await this.$projects.get(this.$route.params.id);
      } catch (err) {
        if (err.error === 'Project not found') {
          this.redirectToNotFound();
        } else if (err.error === 'Content not found') {
          this.redirectToNotUploaded();
        } else if (err.error === 'Site authentication failed') {
          this.redirectToHome();
        } else {
          logger.error(err);
          const msg = err.error || err.message || err;
          this.readerError({
            title: 'Reader error',
            text: msg,
          });
        }
        return;
      }
    }
    await this.parseQueryParameters();

    if (this.layout === '2D') {
      this.$player.createPlayer(this.startMode, this.startUrl);
    }
  },
  computed: {
    ...mapState([
      'autoPlay',
      'regularVideoUrl',
      'projectLoaded',
      'signedSecurity',
      'playState',
      'currentHotspot',
      'currentAttachment',
      'customText',
      'hotspotDescription',
      'project',
      'playbackStarted',
    ]),
    showVideoCover() {
      const {
        contentType,
        signedSecurity,
        playbackStarted,
        autoPlay,
      } = this;
      if (contentType === 'video' && signedSecurity && !playbackStarted && !autoPlay) {
        return true;
      }
      return false;
    },
    layout() {
      return this.project ? this.project.layout : false;
    },
    playerUrl() {
      return `/share/${process.env.VUE_APP_PLAYER_VERSION}/${process.env.VUE_APP_PLAYER_MODE}/player.html`;
    },
    ts() {
      return Date.now();
    },
    contentType() {
      return this.project ? this.project.content : false;
    },
  },
  methods: {
    readerError(evt) {
      EventBus.$emit('reader-dialog', { type: 'error', title: evt.title, text: evt.text });
    },
    async parseQueryParameters() {
      const { q, t } = this.$route.query;

      // Check if t parameter
      if (isNumeric(t)) {
        // Set starttime property
        this.$store.commit('startTime', Number(t));
      }

      const allowedQualities = [
        'dash',
        'low',
        'high',
        'medium',
        'orig',
        '240p',
        '360p',
        '480p',
        '720p',
        '1080p',
        '1440p',
        '2160p',
      ];

      // Check if q parameter
      if (q && allowedQualities.includes(q.toLowerCase())) {
        // Set correct url
        const { project } = this.$store.state;
        let url;
        if (q === 'orig') {
          url = project.file;
        } else if (q === 'low') {
          url = project.low || project['720p'];
        } else if (q === 'medium') {
          url = project.medium || project['1080p'];
        } else if (q === 'high') {
          url = project.high || project['2160p'];
        } else {
          url = project[`${q}`];
        }

        if (url && !(url.includes('.mov'))) {
          // Set autoplay properties
          this.$store.commit('autoPlay', true);
          this.$store.commit('volume', 0);
          this.$store.commit('muted', true);
          const qualityName = q === 'dash' ? 'auto' : q;
          this.$store.commit('selectedQuality', qualityName);
          this.$store.commit('appState', 'LOADING');

          if (this.contentType === 'video') {
            this.startPlayback = true;
          } else {
            this.startPlayback = false;
          }
          this.startUrl = url;
          this.startMode = q === 'dash' ? 'stream' : 'video';
          return;
        } else if (url && url.includes('.mov')) {
          this.readerError({
            title: 'Format not supported',
            text: 'Reader doesn\'t support MOV video format',
          });
        }
      }

      // Set autoplay properties
      this.$store.commit('autoPlay', false);
      this.$store.commit('volume', 1);
      this.$store.commit('muted', false);

      this.startPlayback = false;
      this.startUrl = null;
      this.startMode = null;
    },

    async resetProject(/* to, from */) {
      this.$store.commit('resetState');
      try {
        await this.$projects.get(this.$route.params.id);
      } catch (err) {
        if (err.error === 'Project not found') {
          this.redirectToNotFound();
        } else if (err.error === 'Content not found') {
          this.redirectToNotUploaded();
        } else if (err.error === 'Site authentication failed') {
          this.redirectToHome();
        } else {
          logger.debug(err);
          const msg = err.error || err.message || err;
          this.readerError({
            title: 'Reader error',
            text: msg,
          });
        }
        return;
      }
      await this.parseQueryParameters();

      if (this.layout === '2D') {
        this.$player.createPlayer(this.startMode, this.startUrl);
      }
    },
    redirectToNotFound() {
      this.$router.push({ name: 'NotFound' });
    },
    redirectToNotUploaded() {
      this.$router.push({ name: 'NotUploaded' });
    },
    redirectToHome() {
      this.$router.push({
        name: 'home',
        params: {
          project: this.$route.params.id,
          hint: 'Please provide site password',
        },
      });
    },
    close() {
      this.$store.commit('currentHotspot', null);
    },
    play() {
      this.$player.play();
    },
    pause() {
      this.$player.pause();
    },
    async onLoadIframe() {
      console.log(`player.360 v${process.env.VUE_APP_PLAYER_VERSION}`);
      logger.debug('360 player loaded');
      if (this.startPlayback) {
        this.$player.createPlayer(this.startMode, this.startUrl);
        return;
      }

      if (!this.project) {
        try {
          await this.$projects.get(this.$route.params.id);
        } catch (err) {
          if (err.error === 'Project not found') {
            this.redirectToNotFound();
          } else if (err.error === 'Content not found') {
            this.redirectToNotUploaded();
          } else if (err.error === 'Site authentication failed') {
            this.redirectToHome();
          } else {
            logger.debug(err);
            const msg = err.error || err.message || err;
            this.readerError({
              title: 'Reader error',
              text: msg,
            });
          }
          return;
        }
      }

      if (this.contentType === 'image') {
        const { file } = this.$store.state.project;
        this.$player.createPlayer('image', file);
      }
    },
  },
};
</script>

<style scoped>
.my-container {
  position: absolute;
}

.frame-container {
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
}

.player {
  z-index: -1;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  pointer-events: visible;
}

.regular-projection {
  z-index: -1;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin: 0;
  padding: 0;
  pointer-events: visible;
}

.video-controls {
  position: fixed;
  height: 50px;
  bottom: 10px;
  left: 10px;
  right: 10px;
  z-index: 22;
}

.image-controls {
  position: fixed;
  height: 50px;
  bottom: 10px;
  right: 10px;
  z-index: 22;
}

.webgl-notification {
  position: fixed;
  height: 50px;
  bottom: 10px;
  right: 10px;
  z-index: 23;
}
</style>
